import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Modal, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { calcularSaldoPrestamo } from '../utils/amortizacion';

const SolicitudesPrestamosAdmin = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [registrosMensuales, setRegistrosMensuales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [filterEstado, setFilterEstado] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSolicitudes();
    fetchRegistrosMensuales();
  }, []);

  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/solicitudes-prestamo');
      setSolicitudes(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes de préstamo');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrosMensuales = async () => {
    try {
      const response = await axios.get('/api/registros-mensuales');
      setRegistrosMensuales(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar registros mensuales:', error);
    }
  };

  // Saldo de un préstamo con la misma lógica que el cronograma (respeta a qué
  // préstamo pertenece cada pago; un préstamo finalizado debe $0).
  const calcularSaldoDeSolicitud = (solicitud) => {
    if (!solicitud?.socio?._id) {
      return { capitalPagado: 0, interesPagado: 0, totalPagado: 0, capitalPendiente: solicitud?.monto || 0, totalPendiente: solicitud?.totalAPagar || 0 };
    }

    const registrosDelSocio = registrosMensuales.filter(r => String(r.socio?._id || r.socio) === String(solicitud.socio._id));
    return calcularSaldoPrestamo(solicitud, registrosDelSocio);
  };

  const handleChangeEstado = async (solicitudId, nuevoEstado, confirmarPrestamoMultiple = false) => {
    try {
      const response = await axios.put(`/api/solicitudes-prestamo/${solicitudId}/estado`, {
        estado: nuevoEstado,
        ...(confirmarPrestamoMultiple ? { confirmarPrestamoMultiple: true } : {})
      });
      toast.success('Estado actualizado exitosamente');

      // Actualizar el estado en la lista local
      setSolicitudes(prevSolicitudes =>
        prevSolicitudes.map(s =>
          s._id === solicitudId ? { ...s, estado: nuevoEstado } : s
        )
      );

      // Actualizar el modal si está abierto
      if (selectedSolicitud && selectedSolicitud._id === solicitudId) {
        setSelectedSolicitud(prev => ({ ...prev, estado: nuevoEstado }));
      }
    } catch (error) {
      // El backend avisa con 409 cuando el socio ya tiene otro préstamo
      // activo sin terminar (para no repetir lo que le pasó a Javier
      // Castellanos). Preguntamos si de verdad quiere aprobarlo igual.
      if (error.response?.status === 409 && error.response?.data?.requiereConfirmacion) {
        const confirmar = window.confirm(
          `⚠️ ${error.response.data.message}\n\n¿Aprobar de todas formas?`
        );
        if (confirmar) {
          await handleChangeEstado(solicitudId, nuevoEstado, true);
        }
        return;
      }
      console.error('Error al actualizar estado:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el estado');
    }
  };

  const handleFinalizarPrestamo = async (solicitud) => {
    const { capitalPendiente, totalPendiente } = calcularSaldoDeSolicitud(solicitud);

    let mensaje;
    if (capitalPendiente <= 0.01 && totalPendiente <= 0.01) {
      mensaje = `✅ El préstamo ${solicitud.numeroSolicitud} tiene saldo en 0.\n\n¿Marcarlo como FINALIZADO?`;
    } else {
      mensaje = `⚠️ ATENCIÓN: El préstamo ${solicitud.numeroSolicitud} todavía tiene saldo pendiente:\n\n` +
        `• Capital pendiente: ${formatMoneda(capitalPendiente)}\n` +
        `• Total pendiente: ${formatMoneda(totalPendiente)}\n\n` +
        `¿Deseas marcarlo como FINALIZADO de todas formas?`;
    }

    if (!window.confirm(mensaje)) {
      return;
    }

    await handleChangeEstado(solicitud._id, 'finalizada');
  };

  const handleDeleteSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta solicitud? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axios.delete(`/api/solicitudes-prestamo/${solicitudId}`);
      toast.success('Solicitud eliminada exitosamente');
      fetchSolicitudes();
    } catch (error) {
      console.error('Error al eliminar solicitud:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar la solicitud');
    }
  };

  const handleViewPreview = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setShowPreviewModal(true);
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { variant: 'warning', text: 'Pendiente', icon: 'clock' },
      en_revision: { variant: 'info', text: 'En Revisión', icon: 'search' },
      aprobada: { variant: 'success', text: 'Aprobada', icon: 'check-circle' },
      rechazada: { variant: 'danger', text: 'Rechazada', icon: 'times-circle' },
      desembolsado: { variant: 'primary', text: 'Desembolsado', icon: 'money-bill-wave' },
      finalizada: { variant: 'secondary', text: 'Finalizada', icon: 'flag-checkered' }
    };
    const badge = badges[estado] || badges.pendiente;
    return (
      <Badge bg={badge.variant}>
        <i className={`fas fa-${badge.icon} me-1`}></i>
        {badge.text}
      </Badge>
    );
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMoneda = (monto) => {
    if (isNaN(monto)) return '$0.00';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(monto);
  };

  // Filtrar solicitudes
  const solicitudesFiltradas = solicitudes.filter(s => {
    const cumpleEstado = filterEstado === 'todos' || s.estado === filterEstado;
    const cumpleBusqueda = 
      s.socio?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.socio?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.numeroSolicitud?.toLowerCase().includes(searchTerm.toLowerCase());
    return cumpleEstado && cumpleBusqueda;
  });

  const renderizarPlantilla = (solicitud) => {
    if (!solicitud || !solicitud.plantillaUsada) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '40px', 
          minHeight: '600px',
          fontFamily: 'Arial, sans-serif'
        }}>
          <h3 style={{ textAlign: 'center', marginBottom: '30px' }}>SOLICITUD DE PRÉSTAMO</h3>
          <p style={{ textAlign: 'right' }}>
            Fecha de Solicitud: {new Date(solicitud.fechaSolicitud).toLocaleDateString('es-ES')}<br/>
            Ciudad: {solicitud.datosFormulario?.ciudad || '............................'}
          </p>
          <p style={{ marginTop: '30px' }}>
            Señores<br/>
            UNION FAMILIAS
          </p>
          <p>
            Apellidos y Nombres: {solicitud.socio?.name}<br/>
            Cédula: {solicitud.socio?.cedula}
          </p>
          <p style={{ marginTop: '20px' }}>
            <strong>PRESENTE.-</strong>
          </p>
          <p style={{ textAlign: 'justify', marginTop: '20px' }}>
            Por medio de la presente hago llegar ante usted una solicitud para que me sea otorgada un préstamo personal.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Solicito el préstamo por la cantidad de ${solicitud.monto} comprometiéndome a pagar la totalidad del mismo en un periodo de {solicitud.plazo} meses. Con su respectivo interés.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Esperando poder contar con su ayuda y comprensión.
          </p>
          <div style={{ marginTop: '80px', textAlign: 'center' }}>
            {solicitud.socio?.socioInfo?.firma ? (
              <>
                <img src={solicitud.socio.socioInfo.firma} alt="Firma" style={{ maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto 2px' }} />
                <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 10px' }}></div>
              </>
            ) : (
              <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 10px' }}></div>
            )}
            <p style={{ marginTop: '0px', fontSize: '12px' }}>Firma</p>
          </div>
        </div>
      );
    }

    // Renderizar plantilla estructurada
    const plantilla = solicitud.plantillaUsada;
    const estilos = plantilla.estilos || {};
    const margenSuperior = estilos.margenSuperior || '3';
    const margenes = estilos.margenes || '2';
    const fuenteTitulo = estilos.fuenteTitulo || '16';
    const fuenteTexto = estilos.fuenteTexto || '12';
    const interlineado = estilos.interlineado || '1.5';
    const alineacionGeneral = estilos.alineacionGeneral || 'justify';

    return (
      <div style={{ 
        padding: `${margenSuperior}cm ${margenes}cm`,
        fontSize: `${fuenteTexto}px`,
        lineHeight: interlineado,
        textAlign: alineacionGeneral,
        backgroundColor: 'white',
        minHeight: '600px',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Título */}
        <h3 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          fontSize: `${fuenteTitulo}px`,
          fontWeight: 'bold'
        }}>
          {plantilla.titulo || 'SOLICITUD DE PRÉSTAMO'}
        </h3>

        {/* Encabezado */}
        {plantilla.encabezado && (
          <div style={{ 
            textAlign: plantilla.encabezado.alineacion || 'right',
            marginBottom: '20px'
          }}>
            {plantilla.encabezado.mostrarFecha && (
              <div>
                {plantilla.encabezado.textoFecha || 'Fecha de Solicitud:'} {new Date(solicitud.fechaSolicitud).toLocaleDateString('es-ES')}
              </div>
            )}
            {plantilla.encabezado.mostrarLugar && (
              <div>
                {(() => {
                  let textoLugar = plantilla.encabezado.textoLugar || 'Ciudad:';
                  // Reemplazar variables en el texto del lugar
                  const regex = /\{\{([a-zA-Z_]+)\}\}/g;
                  textoLugar = textoLugar.replace(regex, (match, varName) => {
                    const varLower = varName.toLowerCase();
                    if (varLower === 'ciudad') return solicitud.datosFormulario?.ciudad || '';
                    return '';
                  });
                  return textoLugar || (solicitud.datosFormulario?.ciudad ? solicitud.datosFormulario.ciudad : '............................');
                })()}
              </div>
            )}
          </div>
        )}

        {/* Sección de Datos */}
        {plantilla.seccionDatos && (
          <div style={{ marginTop: '30px', marginBottom: '20px' }}>
            {plantilla.seccionDatos.titulo && (
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {plantilla.seccionDatos.titulo.replace(/\\n/g, '\n')}
              </div>
            )}
            {plantilla.seccionDatos.campos && plantilla.seccionDatos.campos.map((campo, index) => {
              if (!campo.visible) return null;
              
              let valor = campo.variable;
              
              if (valor && valor.includes('{{')) {
                const regex = /\{\{([a-zA-Z_]+)\}\}/g;
                valor = valor.replace(regex, (match, varName) => {
                  const varLower = varName.toLowerCase();
                  if (solicitud.datosFormulario?.[varLower]) return solicitud.datosFormulario[varLower];
                  if (varLower === 'nombre_completo') return solicitud.socio?.name || '';
                  if (varLower === 'cedula') return solicitud.socio?.cedula || '';
                  if (varLower === 'numero_socio') return solicitud.socio?.socioInfo?.numeroSocio || '';
                  if (varLower === 'email') return solicitud.socio?.email || '';
                  if (varLower === 'telefono') return solicitud.socio?.phone || '';
                  if (varLower === 'area') return solicitud.socio?.socioInfo?.area || solicitud.datosFormulario?.area || '';
                  if (varLower === 'cargo') return solicitud.socio?.socioInfo?.cargo || solicitud.datosFormulario?.cargo || '';
                  return '';
                });
              } else {
                // Si no tiene variables, usar datos del usuario según la etiqueta
                const etiqueta = campo.etiqueta.toLowerCase();
                if (etiqueta.includes('nombre')) {
                  valor = solicitud.socio?.name || valor;
                } else if (etiqueta.includes('cédula') || etiqueta.includes('cedula')) {
                  valor = solicitud.socio?.cedula || valor;
                } else if (etiqueta.includes('área') || etiqueta.includes('area')) {
                  valor = solicitud.socio?.socioInfo?.area || solicitud.datosFormulario?.area || valor;
                } else if (etiqueta.includes('cargo')) {
                  valor = solicitud.socio?.socioInfo?.cargo || solicitud.datosFormulario?.cargo || valor;
                }
              }
              
              return (
                <div key={index} style={{ marginBottom: '5px' }}>
                  <strong>{campo.etiqueta}</strong> {valor}
                </div>
              );
            })}
          </div>
        )}

        {/* Saludo */}
        {plantilla.cuerpo?.saludo && (
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <strong>{plantilla.cuerpo.saludo}</strong>
          </div>
        )}

        {/* Párrafos del cuerpo */}
        {plantilla.cuerpo?.parrafos && plantilla.cuerpo.parrafos.map((parrafo, index) => {
          if (!parrafo.visible) return null;
          
          let texto = parrafo.texto;
          const regex = /\{\{([a-zA-Z_]+)\}\}/g;
          const partes = [];
          let lastIndex = 0;
          let match;
          
          while ((match = regex.exec(texto)) !== null) {
            if (match.index > lastIndex) {
              partes.push(texto.substring(lastIndex, match.index));
            }
            
            const varName = match[1].toLowerCase();
            let valor = solicitud.datosFormulario?.[varName];
            
            // Si no está en datosFormulario, buscar en datos del socio
            if (!valor) {
              if (varName === 'nombre_completo' || varName === 'nombresolicuitante') valor = solicitud.socio?.name;
              else if (varName === 'cedula' || varName === 'cedulsolicuitante') valor = solicitud.socio?.cedula;
              else if (varName === 'numero_socio') valor = solicitud.socio?.socioInfo?.numeroSocio;
              else if (varName === 'email') valor = solicitud.socio?.email;
              else if (varName === 'telefono') valor = solicitud.socio?.phone;
              else if (varName === 'area') valor = solicitud.socio?.socioInfo?.area;
              else if (varName === 'cargo') valor = solicitud.socio?.socioInfo?.cargo;
              else if (varName === 'ciudad') valor = solicitud.datosFormulario?.ciudad;
              else if (varName === 'monto') valor = solicitud.monto;
              else if (varName === 'plazo') valor = solicitud.plazo;
              else if (varName === 'cuota_mensual') valor = solicitud.cuotaMensual;
              else if (varName === 'interes_total') valor = solicitud.interesTotal;
              else if (varName === 'total_a_pagar') valor = solicitud.totalAPagar;
              else if (varName === 'fecha_actual') valor = new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            }
            
            partes.push(<strong key={`var-${index}-${match.index}`}>{valor || ''}</strong>);
            
            lastIndex = regex.lastIndex;
          }
          
          if (lastIndex < texto.length) {
            partes.push(texto.substring(lastIndex));
          }
          
          return (
            <p key={index} style={{ 
              textAlign: alineacionGeneral,
              marginBottom: '15px'
            }}>
              {partes}
            </p>
          );
        })}

        {/* Despedida y Firma */}
        {plantilla.despedida && (
          <div style={{ 
            marginTop: '80px',
            textAlign: plantilla.despedida.alineacion || 'center'
          }}>
            {solicitud.socio?.socioInfo?.firma ? (
              <>
                <img src={solicitud.socio.socioInfo.firma} alt="Firma" style={{ maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto 15px' }} />
                <div style={{ 
                  borderTop: '1px solid #000', 
                  width: '200px', 
                  margin: plantilla.despedida.alineacion === 'center' ? '0 auto 10px' : '0 0 10px',
                  marginBottom: '10px'
                }}></div>
              </>
            ) : (
              plantilla.despedida.mostrarLineasFirma && (
                <div style={{ 
                  borderTop: '1px solid #000', 
                  width: '200px', 
                  margin: plantilla.despedida.alineacion === 'center' ? '0 auto 10px' : '0 0 10px',
                  marginBottom: '10px'
                }}></div>
              )
            )}
            <div style={{ fontSize: '12px' }}>{plantilla.despedida.texto || 'Firma'}</div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-3">Cargando solicitudes de préstamo...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-file-invoice-dollar me-2"></i>
                      Solicitudes de Préstamo
                    </h2>
                    <p className="text-muted mb-0">
                      Gestiona todas las solicitudes de préstamo de los socios
                    </p>
                  </div>
                  <div>
                    <Badge bg="primary" className="me-2">
                      Total: {solicitudesFiltradas.length}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre, email o número de solicitud..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <select
                  className="form-select"
                  value={filterEstado}
                  onChange={(e) => setFilterEstado(e.target.value)}
                >
                  <option value="todos">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_revision">En Revisión</option>
                  <option value="aprobada">Aprobada</option>
                  <option value="rechazada">Rechazada</option>
                  <option value="desembolsado">Desembolsado</option>
                  <option value="finalizada">Finalizada</option>
                </select>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Solicitudes */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Listado de Solicitudes
                </h5>
              </Card.Header>
              <Card.Body>
                {solicitudesFiltradas.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No hay solicitudes que coincidan con los filtros aplicados.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>N° Solicitud</th>
                          <th>Socio</th>
                          <th>Fecha</th>
                          <th>Monto</th>
                          <th>Plazo</th>
                          <th>Cuota Mensual</th>
                          <th>Pagado</th>
                          <th>Pendiente</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {solicitudesFiltradas.map((solicitud) => {
                          const saldo = calcularSaldoDeSolicitud(solicitud);
                          const estaPagado = saldo.totalPendiente <= 0.01;
                          const aplicaPagos = ['aprobada', 'desembolsado', 'finalizada'].includes(solicitud.estado);
                          return (
                          <tr key={solicitud._id}>
                            <td>
                              <strong>{solicitud.numeroSolicitud}</strong>
                            </td>
                            <td>
                              <div>
                                <strong>{solicitud.socio?.name}</strong>
                                <br />
                                <small className="text-muted">{solicitud.socio?.email}</small>
                              </div>
                            </td>
                            <td>{formatFecha(solicitud.fechaSolicitud)}</td>
                            <td>
                              <strong className="text-primary">
                                {formatMoneda(solicitud.monto)}
                              </strong>
                            </td>
                            <td>{solicitud.plazo} meses</td>
                            <td>
                              <strong className="text-success">
                                {formatMoneda(solicitud.cuotaMensual)}
                              </strong>
                            </td>
                            <td>
                              {aplicaPagos ? (
                                <div>
                                  <strong className="text-success">{formatMoneda(saldo.totalPagado)}</strong>
                                  <br />
                                  <small className="text-muted">
                                    Cap: {formatMoneda(saldo.capitalPagado)}
                                  </small>
                                </div>
                              ) : (
                                <small className="text-muted">—</small>
                              )}
                            </td>
                            <td>
                              {aplicaPagos ? (
                                <div>
                                  <strong className={estaPagado ? 'text-success' : 'text-danger'}>
                                    {formatMoneda(saldo.totalPendiente)}
                                  </strong>
                                  <br />
                                  <small className="text-muted">
                                    Cap: {formatMoneda(saldo.capitalPendiente)}
                                  </small>
                                  {estaPagado && (
                                    <div>
                                      <Badge bg="success" className="mt-1">
                                        <i className="fas fa-check me-1"></i>Pagado
                                      </Badge>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <small className="text-muted">—</small>
                              )}
                            </td>
                            <td>{getEstadoBadge(solicitud.estado)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                title="Ver detalles"
                                onClick={() => handleViewPreview(solicitud)}
                                className="me-2"
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                              {solicitud.estado === 'pendiente' && (
                                <div className="btn-group me-2" role="group">
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    title="Aprobar"
                                    onClick={() => handleChangeEstado(solicitud._id, 'aprobada')}
                                  >
                                    <i className="fas fa-check"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    title="Rechazar"
                                    onClick={() => handleChangeEstado(solicitud._id, 'rechazada')}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </div>
                              )}
                              {(solicitud.estado === 'aprobada' || solicitud.estado === 'desembolsado') && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  title="Marcar como Finalizado (préstamo pagado en su totalidad)"
                                  onClick={() => handleFinalizarPrestamo(solicitud)}
                                  className="me-2"
                                >
                                  <i className="fas fa-flag-checkered"></i>
                                </Button>
                              )}
                              <Button
                                variant="outline-warning"
                                size="sm"
                                title="Ver detalles del crédito"
                                onClick={() => {
                                  setSelectedSolicitud(solicitud);
                                  setShowDetallesModal(true);
                                }}
                                className="me-2"
                              >
                                <i className="fas fa-info-circle"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title="Eliminar solicitud"
                                onClick={() => handleDeleteSolicitud(solicitud._id)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal de Vista Previa */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" fullscreen="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            Vista Previa de Solicitud de Préstamo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f5f5f5', padding: '20px' }}>
          {selectedSolicitud && renderizarPlantilla(selectedSolicitud)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Detalles del Crédito */}
      <Modal show={showDetallesModal} onHide={() => setShowDetallesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calculator me-2"></i>
            Detalles del Crédito
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSolicitud && (
            <div>
              <Card className="mb-3 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-file-invoice me-2"></i>
                    Información de la Solicitud
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Número de Solicitud</small>
                        <h5 className="mb-0">{selectedSolicitud.numeroSolicitud}</h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Socio</small>
                        <h5 className="mb-0">{selectedSolicitud.socio?.name}</h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Cédula</small>
                        <h5 className="mb-0">{selectedSolicitud.socio?.cedula}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Fecha de Solicitud</small>
                        <h5 className="mb-0">{formatFecha(selectedSolicitud.fechaSolicitud)}</h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Estado</small>
                        <div className="mt-1">{getEstadoBadge(selectedSolicitud.estado)}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-money-bill-wave me-2"></i>
                    Detalles del Crédito
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Monto Solicitado</small>
                        <h4 className="text-primary mb-0">
                          {formatMoneda(selectedSolicitud.monto)}
                        </h4>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Plazo</small>
                        <h5 className="text-info mb-0">
                          {selectedSolicitud.plazo} meses
                        </h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Tasa de Interés Mensual</small>
                        <h5 className="text-warning mb-0">
                          {selectedSolicitud.tasaInteres}%
                        </h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Cuota Mensual</small>
                        <h4 className="text-success mb-0">
                          {formatMoneda(selectedSolicitud.cuotaMensual)}
                        </h4>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Interés Total</small>
                        <h5 className="text-danger mb-0">
                          {formatMoneda(selectedSolicitud.interesTotal)}
                        </h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Total a Pagar</small>
                        <h4 className="text-dark mb-0">
                          {formatMoneda(selectedSolicitud.totalAPagar)}
                        </h4>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Alert variant="info">
                <div className="mb-2">
                  <strong><i className="fas fa-file-invoice me-1"></i> Resumen del Crédito:</strong>
                </div>
                <small>
                  El socio está solicitando un préstamo de <strong>{formatMoneda(selectedSolicitud.monto)}</strong> a pagar en <strong>{selectedSolicitud.plazo} meses</strong> con una tasa de interés del <strong>{selectedSolicitud.tasaInteres}%</strong> mensual. 
                  Esto significa que pagará una cuota fija de <strong>{formatMoneda(selectedSolicitud.cuotaMensual)}</strong> cada mes durante {selectedSolicitud.plazo} meses. 
                  Al finalizar el plazo, habrá pagado un total de <strong>{formatMoneda(selectedSolicitud.totalAPagar)}</strong>, 
                  de los cuales <strong>{formatMoneda(selectedSolicitud.interesTotal)}</strong> corresponden a intereses.
                </small>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetallesModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SolicitudesPrestamosAdmin;
